import type { EntityId, Percent, ReadonlyVec2 } from "@core/types";
import type { Collectible } from "@entities/collectible";
import type { Player } from "@entities/player";
import {
  FLOWER_USE_RADIUS,
  GLOWFLY_COLLECT_RADIUS,
  SAND_COLLECT_RADIUS,
  SEED_COLLECT_RADIUS,
  SEED_REVEAL_RADIUS,
} from "@data/balance";
import { prefersReducedMotion } from "./reduced-motion";

/** Целевые DOM-элементы HUD. */
export interface HudTargets {
  energyFill: HTMLElement;
  energyText: HTMLElement;
  seedCount: HTMLElement;
  glowflyCount: HTMLElement;
  message: HTMLElement;
}

/** Все DOM-узлы, нужные рендереру. */
export interface RenderTargets {
  stage: HTMLElement;
  collectiblesLayer: HTMLElement;
  boo: HTMLElement;
  lightField: HTMLElement;
  celebration: HTMLElement;
  hud: HudTargets;
  joystickBase: HTMLElement;
  joystickKnob: HTMLElement;
  glowButton: HTMLElement;
}

/** Собирает RenderTargets из корня документа. Бросает ясную ошибку если узел отсутствует. */
export const bindRenderTargets = (root: ParentNode): RenderTargets => {
  const required = (id: string): HTMLElement => {
    const el = root.querySelector<HTMLElement>(`#${id}`);
    if (!el) throw new Error(`bindRenderTargets: element #${id} not found`);
    return el;
  };

  return {
    stage: required("stage"),
    collectiblesLayer: required("collectibles"),
    boo: required("boo"),
    lightField: required("lightField"),
    celebration: required("celebration"),
    hud: {
      energyFill: required("energyFill"),
      energyText: required("energyText"),
      seedCount: required("seedCount"),
      glowflyCount: required("glowflyCount"),
      message: required("message"),
    },
    joystickBase: required("stickBase"),
    joystickKnob: required("stickKnob"),
    glowButton: required("glowButton"),
  };
};

const distance = (a: ReadonlyVec2, b: ReadonlyVec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Рендерер DOM. Единственное место, где разрешено мутировать стили. */
export class DomRenderer {
  private readonly collectibleNodes = new Map<EntityId, HTMLElement>();
  private messageHideTimer: number | null = null;
  private booJoyTimer: number | null = null;
  private lastMessage = "";

  constructor(private readonly targets: RenderTargets) {}

  /**
   * Обновляет позицию и анимацию персонажа Boo.
   * elapsedMs — накопленное время в миллисекундах (elapsed * 1000).
   */
  renderPlayer(player: Player, elapsedMs: number): void {
    const { boo, lightField, stage } = this.targets;
    const { x, y } = player.position;

    boo.style.left = `${x}%`;
    boo.style.top = `${y}%`;
    lightField.style.left = `${x}%`;
    lightField.style.top = `${y}%`;

    stage.style.setProperty("--boo-x", `${x}%`);
    stage.style.setProperty("--boo-y", `${y}%`);

    const scaleX = player.facing === "left" ? -1 : 1;
    const bobY = player.isMoving ? Math.sin(elapsedMs / 160) * 1.5 : 0;
    boo.style.transform = `translate(-50%, -50%) scaleX(${scaleX}) translateY(${bobY}px)`;
  }

  /**
   * Обновляет видимость и позицию всех collectibles.
   * Монтирует ноды, которых ещё нет в DOM.
   */
  renderCollectibles(items: readonly Collectible[], player: ReadonlyVec2, glow: boolean): void {
    for (const item of items) {
      if (!this.collectibleNodes.has(item.id)) {
        this.mountCollectible(item);
      }
      const el = this.collectibleNodes.get(item.id);
      if (!el) continue;

      const d = distance(player, item.position);
      // pickup-ready: предмет внутри радиуса сбора и ещё активен (включая seed,
      // которому дополнительно нужен glow для становления visible)
      let pickupReady = false;

      if (item.type === "seed") {
        if (item.collected) {
          el.classList.add("collected");
          el.classList.remove("visible", "is-pickup-ready");
        } else {
          const visible = glow && d < SEED_REVEAL_RADIUS;
          el.classList.toggle("visible", visible);
          pickupReady = visible && d < SEED_COLLECT_RADIUS * 1.4;
        }
      } else if (item.type === "sand") {
        if (item.collected) {
          el.classList.add("collected");
          el.classList.remove("is-pickup-ready");
        } else {
          pickupReady = d < SAND_COLLECT_RADIUS * 1.4;
        }
      } else if (item.type === "flower") {
        if (item.used) {
          el.classList.add("used");
          el.classList.remove("is-pickup-ready");
        } else {
          pickupReady = d < FLOWER_USE_RADIUS * 1.4;
        }
      } else if (item.type === "glowfly") {
        if (item.collected) {
          el.classList.add("collected");
          el.classList.remove("is-pickup-ready");
        } else {
          el.style.left = `${item.position.x}%`;
          el.style.top = `${item.position.y}%`;
          pickupReady = d < GLOWFLY_COLLECT_RADIUS * 1.4;
        }
      }

      if (!el.classList.contains("collected") && !el.classList.contains("used")) {
        el.classList.toggle("is-pickup-ready", pickupReady);
      }
    }
  }

  /** Создаёт DOM-элемент для collectible и добавляет в слой. */
  mountCollectible(c: Collectible): HTMLElement {
    const el = document.createElement("div");
    el.className = c.type;
    el.dataset.id = c.id;
    el.style.left = `${c.position.x}%`;
    el.style.top = `${c.position.y}%`;
    this.targets.collectiblesLayer.appendChild(el);
    this.collectibleNodes.set(c.id, el);
    return el;
  }

  /** Удаляет DOM-элемент collectible из слоя. */
  unmountCollectible(id: EntityId): void {
    const el = this.collectibleNodes.get(id);
    if (el) {
      el.remove();
      this.collectibleNodes.delete(id);
    }
  }

  /**
   * Обновляет горизонтальный energy bar и текстовый процент.
   * energy = 0..100. fill.style.width в процентах.
   */
  renderEnergy(energy: Percent): void {
    const rounded = Math.round(energy);
    this.targets.hud.energyFill.style.width = `${rounded}%`;
    this.targets.hud.energyText.textContent = `${rounded}%`;
  }

  /** Обновляет счётчик собранных семян ("collected/total"). */
  renderSeeds(collected: number, total: number): void {
    this.targets.hud.seedCount.textContent = `${collected}/${total}`;
  }

  /** Обновляет счётчик пойманных glowflies ("collected/total"). */
  renderGlowflies(collected: number, total: number): void {
    this.targets.hud.glowflyCount.textContent = `${collected}/${total}`;
  }

  /**
   * Обновляет текстовое сообщение в HUD с автоскрытием через 3.5с.
   * Debounce: новое сообщение сбрасывает таймер.
   */
  renderMessage(text: string): void {
    if (this.lastMessage === text) return;
    this.lastMessage = text;
    this.targets.hud.message.textContent = text;
    this.targets.hud.message.classList.add("is-visible");

    if (this.messageHideTimer !== null) {
      window.clearTimeout(this.messageHideTimer);
    }

    this.messageHideTimer = window.setTimeout(() => {
      this.targets.hud.message.classList.remove("is-visible");
      this.messageHideTimer = null;
    }, 3500);
  }

  /** Переключает glow-состояние визуально (класс glow-on на stage и active на glowButton). */
  setGlowState(active: boolean): void {
    this.targets.stage.classList.toggle("glow-on", active);
    this.targets.glowButton.classList.toggle("active", active);
  }

  /**
   * Спавнит bloom-burst элементы для финальной анимации.
   * points — массив позиций в % от stage.
   */
  spawnBloomBursts(points: readonly ReadonlyVec2[]): HTMLElement[] {
    const result: HTMLElement[] = [];
    points.forEach((pt, index) => {
      const bloom = document.createElement("span");
      bloom.className = "bloom";
      bloom.style.left = `${pt.x}%`;
      bloom.style.top = `${pt.y}%`;
      bloom.style.animationDelay = `${index * 70}ms`;
      this.targets.celebration.appendChild(bloom);
      result.push(bloom);
    });
    return result;
  }

  /**
   * Создаёт .glow-particle элемент вокруг позиции игрока.
   * Удаляет себя по animationend.
   */
  spawnGlowParticle(position: ReadonlyVec2): void {
    const particle = document.createElement("span");
    const angle = Math.random() * Math.PI * 2;
    const radius = 3 + Math.random() * 8;
    particle.className = "glow-particle";
    particle.style.left = `${position.x + Math.cos(angle) * radius * 0.55}%`;
    particle.style.top = `${position.y + Math.sin(angle) * radius * 0.4}%`;
    particle.style.animationDuration = `${760 + Math.random() * 440}ms`;
    this.targets.stage.appendChild(particle);
    particle.addEventListener(
      "animationend",
      () => {
        particle.remove();
      },
      { once: true },
    );
  }

  /** Переключает CSS-класс на stage элементе. */
  setStageClass(className: string, on: boolean): void {
    this.targets.stage.classList.toggle(className, on);
  }

  /** Возвращает DOM-элемент collectible по EntityId, или null если не найден. */
  getCollectibleNode(id: EntityId): HTMLElement | null {
    return this.collectibleNodes.get(id) ?? null;
  }

  /**
   * Запускает радостную squash-and-stretch анимацию Boo и спавнит 6 искорок
   * вокруг персонажа. Триггерится из GameScene при сборе любого предмета.
   * При prefers-reduced-motion — no-op.
   */
  playBooJoy(position: ReadonlyVec2): void {
    if (prefersReducedMotion()) return;
    const svg = this.targets.boo.querySelector<SVGSVGElement>(".boo-svg");
    if (svg) {
      svg.classList.remove("is-joyful");
      // Force reflow чтобы анимация перезапустилась при частых сборах
      void svg.getBoundingClientRect();
      svg.classList.add("is-joyful");
      if (this.booJoyTimer !== null) window.clearTimeout(this.booJoyTimer);
      this.booJoyTimer = window.setTimeout(() => {
        svg.classList.remove("is-joyful");
        this.booJoyTimer = null;
      }, 600);
    }

    // 6 искорок, разлетающихся в случайных направлениях
    for (let i = 0; i < 6; i++) {
      this.spawnJoySpark(position, i);
    }
  }

  /** Спавнит одну joy-искорку вокруг позиции Boo. */
  private spawnJoySpark(position: ReadonlyVec2, index: number): void {
    const spark = document.createElement("span");
    spark.className = "joy-spark";
    const angle = (index / 6) * Math.PI * 2 + Math.random() * 0.6;
    const distancePx = 28 + Math.random() * 22;
    const tx = Math.cos(angle) * distancePx;
    const ty = Math.sin(angle) * distancePx - 14;
    spark.style.left = `${position.x}%`;
    spark.style.top = `${position.y}%`;
    spark.style.setProperty("--joy-tx", `${tx}px`);
    spark.style.setProperty("--joy-ty", `${ty}px`);
    this.targets.stage.appendChild(spark);
    spark.addEventListener(
      "animationend",
      () => {
        spark.remove();
      },
      { once: true },
    );
  }

  /**
   * Размонтирует все collectible ноды из DOM и очищает внутренний кеш.
   * Используется при сбросе игры для переинициализации предметов.
   */
  unmountAllCollectibles(): void {
    for (const el of this.collectibleNodes.values()) {
      el.remove();
    }
    this.collectibleNodes.clear();
  }
}
